import React, { useEffect, useState } from "react";

import { Button, DatePicker, IconWrapper, Select, TextArea, TextField, Typography } from "@king-ict/eupisi-ui/components";
import { ReactComponent as CloseIcon } from "@king-ict/eupisi-ui/components/Icon/CloseBig.svg";
import {
  useValidation,
  useDisableBodyScroll,
  enableBodyScroll,
} from "@king-ict/eupisi-ui/hooks";
import { DocumentData, CodeBook } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { DocumentUploadRequest, uploadDocument } from "../../api/nisooApi";
import { useInput } from "../../hooks";
import { DocumentType } from "../../models/Document";
import { ProgramType } from "../../models/Program";
import { ExecutionResolutionDocument, ResolutionDocumentValidationScheme } from "../../models/ProgramRequest";
import { ResolutionDocumentType } from "../../models/ProgramRequest/ResolutionDocumentType";
import UploadButton from "../UploadButton";
import styles from "./ModalResolution.module.scss";

export interface ModalResolutionProps {
  children?: any;
  title?: string;
  id?: string;
  open: boolean;
  executionDocumentData?: ExecutionResolutionDocument;
  minWidth?: boolean;
  showCloseMark?: boolean;
  hideOverlay?: boolean;
  disableBody?: boolean;
  type?: "default" | "confirm" | "message" | "error";
  variant?: "normal" | "medium" | "big";
  confirmText?: string;
  rejectText?: string;
  onConfirm?: (approved: boolean, resolutionData: ExecutionResolutionDocument) => void;
  cancelText?: string;
  captions: string[];
  onClose: (event: React.MouseEvent<any, MouseEvent>) => void;
  showComment?: boolean,
  forceComment?: boolean,
  documentToolTip: string,
  documentFilter: string,
  resolutionDoc: ExecutionResolutionDocument,
  showProcessingDeadline?: boolean
}

export const ModalResolution = ({
  children,
  title = "",
  id,
  open = false,
  // documentTypeId,
  executionDocumentData,
  minWidth = true,
  hideOverlay = false,
  disableBody = true,
  type = "default",
  variant = "normal",
  showCloseMark = type === "default" ? true : false,
  confirmText,
  rejectText,
  onConfirm,
  cancelText,
  captions,
  onClose,
  showComment = false,
  documentToolTip,
  documentFilter,
  resolutionDoc,
  showProcessingDeadline = false
}: ModalResolutionProps) => {
  useDisableBodyScroll(disableBody, open);

  const { t } = useTranslation();
  const { handleChange } = useInput();

  const [resolution, setResolution] = useState<ExecutionResolutionDocument>(resolutionDoc);
  const [showCommentInternal, setShowCommentInternal] = useState<boolean>(showComment);
  const [captionsInternal, setCaptionsInternal] = useState<string[]>(captions);
    
  
  const [errors, doValidations] = useValidation({
    data: resolution,
    validationSchema: ResolutionDocumentValidationScheme,
  });

  useEffect(() => {
    //unmount
    return () => {
      enableBodyScroll();
    };
  }, []);

  useEffect(() => {
    if (executionDocumentData) {
      setResolution(executionDocumentData);
      // is there a way to reset the validation object?
    }
  }, [executionDocumentData, resolutionDoc]);

  useEffect(() => {
    if (captions)
      setCaptionsInternal(captions);
  }, [captions])

  useEffect(() => {
    setShowCommentInternal(showComment);
  }, [showComment])

  const clearModal = (e : React.MouseEvent<any, MouseEvent>) => {    
    // setResolution(executionDocumentData);        
    onClose(e);    
  };

  const handleConfirm = (approved: boolean) => {
    const isValid = doValidations();
    if (!isValid) {
      return;
    }
    if (onConfirm) {
      onConfirm(approved, resolution);
    }
  }

  const handleGetActions = () => {
    switch (type) {
      case "default":
        return <></>;
      case "confirm":
        return (
          <div className={styles.confirmButtonsContainer}>
            <div>
              <Button color="primary" outline onClick={(e) => {clearModal(e)}}>
                {cancelText}
              </Button>
            </div>
            <div>
              {rejectText &&
                <Button className={styles.rejectButtons} onClick={() => handleConfirm(false)}>
                  {rejectText}
                </Button>
              }
              <Button color="primary" onClick={() => handleConfirm(true)}>
                {confirmText}
              </Button>
            </div>
          </div>
        );
    }
  };

  function getVariant() {
    if (variant) {
      return styles[variant];
    }
  }

    const handleDocumentAdded = async (event: React.ChangeEvent<HTMLInputElement>, file: File) => {      

    	var resolutionDocument: DocumentData = {
        id: -1,
        fileName: file.name,
        file: file
      };
      setResolution((prev) => ({
        ...prev,
        resolutionDocument: resolutionDocument
      }));
  };

  const handleDocumentRemoved = async (event: React.ChangeEvent<HTMLInputElement>) => {
    let resolutionDocument = resolution.resolutionDocument;
    if (resolutionDocument === undefined || resolutionDocument.id === undefined) {
      return;
    }

    resolutionDocument = undefined;

    setResolution((prev) => ({
      ...prev,
      resolutionDocument: resolutionDocument
    }));
  };

  const getLabelText = (labelIndex: number) => {
    if (captionsInternal.length > labelIndex)
      return captionsInternal[labelIndex];

    return "";
  };

  const getCommentBox = () => {
    return (
      <TextArea
        label={t("request.resolutionModalComment")}
        ariaLabel={t("request.rejectRequestModalNote")}
        value={resolution.description}
        name={nameof<typeof resolution>("description")}
        maxLength={3600}
        rows={1}
        onChange={handleChange(setResolution)}
        errorText={errors?.description}
      />
    );
  };

  if (open === false) return null;
  return (
    <div
      className={`${styles.parentContainer} ${type === "error" ? styles.error : ""
        }`}
    >
      <div
        className={`${styles.overlay} ${hideOverlay ? styles.off : styles.on}`}
        onClick={onClose}
      ></div>
      <div
        id={id}
        className={`${styles.container} ${type === "error" ? styles.error : ""
          } ${getVariant()} ${minWidth ? styles.minWidth : ""}`}
      >
        <div className={styles.contentWrapper}>
          {title || showCloseMark ? (
            <div className={styles.header}>
              <div className={styles.title}>
                <Typography variant="largeBold">{title}</Typography>
              </div>
              <div className={styles.closeMark} onClick={onClose}>
                {showCloseMark ? (
                  <IconWrapper color="black-color">
                    <CloseIcon aria-label={t("accessibility.closeIcon")} />
                  </IconWrapper>
                ) : (
                  <></>
                )}
              </div>
            </div>
          ) : (
            <></>
          )}
          <div className={styles.content}>{children}</div>
          <Row className="">
            <Col md={12}>
              <div
              >
                <Row>
                  <Col md={12}>
                    <UploadButton
                      label={getLabelText(0)}
                      className={styles.uploadButtonInRowSection}
                      value={resolution.resolutionDocument}
                      name={nameof<typeof resolution>("resolutionDocument")}
                      onFileAdded={(file, e) => handleDocumentAdded(e, file)}
                      onFileRemoved={(e) => handleDocumentRemoved(e)}
                      toolTip={documentToolTip}
                      fileFilter={documentFilter}
                      errorText={errors?.resolutionDocument}
                      documentType={DocumentType.ProgramExecutionResolution}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <TextField
                      label={getLabelText(1)}
                      value={resolution.classificationMark}
                      name={nameof<typeof resolution>("classificationMark")}
                      maxLength={128}
                      onChange={handleChange(setResolution)}
                      errorText={errors?.classificationMark}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <TextField
                      label={getLabelText(2)}
                      value={resolution.registryNumber}
                      name={nameof<typeof resolution>("registryNumber")}
                      maxLength={128}
                      onChange={handleChange(setResolution)}
                      errorText={errors?.registryNumber}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <DatePicker
                      label={getLabelText(3)}
                      value={resolution.issueDate}
                      name={nameof<typeof resolution>("issueDate")}
                      onChange={handleChange(setResolution)}
                      minYear={new Date().getFullYear() - 25}
                      maxYear={new Date().getFullYear() + 25}
                    />
                  </Col>
                </Row>
                {showProcessingDeadline && 
                  <Row>
                    <Col md={12}>
                    <TextField
                      label={t("request.resolutionModalProcessingDeadline")}
                      name={nameof<typeof resolution>("processingDeadline")}
                      type="number"
                      value={resolution.processingDeadline}
                      onChange={handleChange(setResolution)}
                      errorText={errors?.processingDeadline}                      
                    />
                    </Col>
                  </Row>                 
                }
                {showCommentInternal && 
                <TextArea
                  label={t("request.resolutionModalComment")}
                  ariaLabel={t("request.rejectRequestModalNote")}
                  value={resolution.description}
                  name={nameof<typeof resolution>("description")}
                  maxLength={3600}
                  rows={1}
                  onChange={handleChange(setResolution)}
                  errorText={errors?.description}
                />
                }
              </div>
            </Col>
          </Row>
        </div>
        {handleGetActions()}
      </div>
    </div>
  );
};
